import {
  Button,
  CustomFilter,
  DatePicker,
  EllipsisSpan,
  Form,
  Input,
  Modal,
  Paging,
  Select,
  Table,
  Tag,
  Wrapper,
  message,
  useBreadcrumbRoutes,
  usePaging,
} from '@maxtropy/components';
import { ColumnType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Key, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MOCK_ALARMS } from './mockData';
import styles from './index.module.scss';
import { AlarmFilters, AlarmLevel, DeviationAlarmRecord, MonitorType } from './types';

const { RangePicker } = DatePicker;

const ALARM_LEVEL_CONFIG: Record<AlarmLevel, { label: string; color: string }> = {
  [AlarmLevel.LOWEST]: { label: '最低级', color: '#A0D911' },
  [AlarmLevel.LOW]: { label: '低级', color: '#52C41A' },
  [AlarmLevel.MEDIUM]: { label: '中级', color: '#2D8DFF' },
  [AlarmLevel.HIGH]: { label: '高级', color: '#FAAD14' },
  [AlarmLevel.HIGHEST]: { label: '最高级', color: '#FF4D4F' },
};

const MONITOR_TYPE_CONFIG: Record<MonitorType, string> = {
  [MonitorType.PV]: '光伏',
  [MonitorType.LOAD]: '负荷',
  [MonitorType.THERMAL]: '火电',
  [MonitorType.STORAGE]: '储能',
};

const alarmLevelOptions = Object.entries(ALARM_LEVEL_CONFIG).map(([value, config]) => ({
  value,
  label: config.label,
}));

const monitorTypeOptions = Object.entries(MONITOR_TYPE_CONFIG).map(([value, label]) => ({
  value,
  label,
}));

const DeviationAlarm = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const navigate = useNavigate();
  const [form] = Form.useForm<AlarmFilters>();
  const [records, setRecords] = useState(MOCK_ALARMS);
  const [filters, setFilters] = useState<AlarmFilters>({});
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const pagingInfo = usePaging(10);
  const { pageOffset, pageSize, setPageOffset, setTotalCount } = pagingInfo;

  const monitorObjectOptions = useMemo(
    () =>
      Array.from(new Set(records.map(item => item.monitorObject))).map(value => ({
        label: value,
        value,
      })),
    [records]
  );

  const filteredRecords = useMemo(() => {
    const keyword = filters.alarmInfo?.trim().toLowerCase();
    const [startDate, endDate] = filters.alarmTime ?? [];

    return records.filter(item => {
      const alarmInfo = `实际执行与调度建议偏差率大于 ${item.threshold.toFixed(2)}%`;
      const alarmDate = dayjs(item.alarmTime);
      const matchesInfo = !keyword || alarmInfo.toLowerCase().includes(keyword);
      const matchesType = !filters.monitorType || item.monitorType === filters.monitorType;
      const matchesObject = !filters.monitorObject || item.monitorObject === filters.monitorObject;
      const matchesLevel = !filters.alarmLevel || item.alarmLevel === filters.alarmLevel;
      const matchesStart = !startDate || !alarmDate.isBefore(startDate.startOf('day'));
      const matchesEnd = !endDate || !alarmDate.isAfter(endDate.endOf('day'));

      return matchesInfo && matchesType && matchesObject && matchesLevel && matchesStart && matchesEnd;
    });
  }, [filters, records]);

  useEffect(() => {
    setTotalCount(filteredRecords.length);
    const lastPage = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
    if (pageOffset > lastPage) {
      setPageOffset(lastPage);
    }
  }, [filteredRecords.length, pageOffset, pageSize, setPageOffset, setTotalCount]);

  const pageData = useMemo(() => {
    const start = (pageOffset - 1) * pageSize;
    return filteredRecords.slice(start, start + pageSize);
  }, [filteredRecords, pageOffset, pageSize]);

  const handleBatchDelete = () => {
    Modal.confirm({
      title: '确认删除选中的报警记录？',
      content: '删除后不可恢复。',
      okText: '确定',
      cancelText: '取消',
      onOk: () => {
        setRecords(current => current.filter(item => !selectedRowKeys.includes(item.id)));
        setSelectedRowKeys([]);
        message.success('删除成功');
      },
    });
  };

  const columns = useMemo<ColumnType<DeviationAlarmRecord>[]>(
    () => [
      {
        title: '报警信息',
        dataIndex: 'threshold',
        width: 290,
        render: (value: number) => (
          <div className={styles.alarmInfo}>
            实际执行与调度建议偏差率大于 <span className={styles.threshold}>{value.toFixed(2)}%</span>
          </div>
        ),
      },
      {
        title: '监听对象类型',
        dataIndex: 'monitorType',
        width: 130,
        render: (value: MonitorType) => <EllipsisSpan value={MONITOR_TYPE_CONFIG[value]} />,
      },
      {
        title: '监听对象',
        dataIndex: 'monitorObject',
        width: 140,
        ellipsis: true,
        render: (value: string) => <EllipsisSpan value={value} />,
      },
      {
        title: '报警规则',
        dataIndex: 'alarmRule',
        width: 130,
        ellipsis: true,
        render: (value: string) => <EllipsisSpan value={value} />,
      },
      {
        title: '报警等级',
        dataIndex: 'alarmLevel',
        width: 110,
        render: (value: AlarmLevel) => {
          const config = ALARM_LEVEL_CONFIG[value];
          return (
            <Tag border="solid" color={config.color} className={styles.tag}>
              {config.label}
            </Tag>
          );
        },
      },
      {
        title: '偏差率',
        dataIndex: 'deviationRate',
        width: 110,
        render: (value: number) => <span className={styles.deviation}>{value.toFixed(2)}%</span>,
      },
      {
        title: '报警时间',
        dataIndex: 'alarmTime',
        width: 190,
        render: (value: string) => <EllipsisSpan value={value} />,
      },
      {
        title: '操作',
        key: 'action',
        fixed: 'right',
        width: 90,
        render: (_, record) => (
          <Button
            type="link"
            className={styles.actionButton}
            onClick={() => navigate(`/dispatch/control/deviation-alarm/${record.id}/view`)}
          >
            查看
          </Button>
        ),
      },
    ],
    [navigate]
  );

  const filterContent = (
    <CustomFilter<AlarmFilters>
      form={form}
      onFinish={values => {
        setPageOffset(1);
        setFilters(values);
      }}
      onReset={() => {
        setPageOffset(1);
        setFilters({});
      }}
    >
      <Form.Item name="alarmInfo" label="报警信息">
        <Input allowClear placeholder="请输入" />
      </Form.Item>
      <Form.Item name="monitorType" label="监听对象类型">
        <Select allowClear placeholder="请选择" options={monitorTypeOptions} />
      </Form.Item>
      <Form.Item name="monitorObject" label="监听对象">
        <Select allowClear showSearch optionFilterProp="label" placeholder="请选择" options={monitorObjectOptions} />
      </Form.Item>
      <Form.Item name="alarmTime" label="报警时间">
        <RangePicker style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="alarmLevel" label="报警等级">
        <Select allowClear placeholder="请选择" options={alarmLevelOptions} />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <Wrapper routes={breadcrumbRoutes?.routes ?? []} filters={filterContent} className={styles.wrapper}>
      <div className={styles.toolbar}>
        <Button type="primary" disabled={!selectedRowKeys.length} onClick={handleBatchDelete}>
          批量删除
        </Button>
      </div>
      <Table
        sticky
        rowKey="id"
        scroll={{ x: 1280 }}
        rowSelection={{
          selectedRowKeys,
          preserveSelectedRowKeys: true,
          onChange: keys => setSelectedRowKeys(keys),
        }}
        columns={columns}
        dataSource={pageData}
      />
      <Paging pagingInfo={pagingInfo} />
    </Wrapper>
  );
};

export default DeviationAlarm;
